/**
 * SidePanelChat — Adapts the existing ChatInterface for the
 * right-side panel in the AppShell layout.
 *
 * Reuses the same API hooks, subscriptions, and session management
 * from ChatInterface but renders in a compact side-panel format
 * that matches the new design system.
 *
 * Props:
 *   open: boolean — whether the panel is visible
 *   onClose: () => void — close the panel
 *   onOpenDocument: (doc) => void — show a Livi file chip's document in the center panel
 */

import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { sendChatMessageV2, sendReplyFeedback, useGetChatHistory, useGetChatList } from '../../../api/chat';
import { useChatSubscription } from '../../../hooks/useChatSubscription';
import UserProfileContext from '../../../context/userprofilecontext';
import { formatToolName } from '../../../utils/toolNames';
import { normalizeChatText } from '../../../utils/chatText';
import { getInitials } from '../../../utils/formatters';
import styles from './SidePanelChat.module.css';

const generatesessionID = () => crypto.randomUUID();

// Full display name for the logged-in user, mirroring NavSidebar so the chat
// avatar/title matches the sidebar instead of falling back to a bare userID.
// firstName+lastName → "Tukta Belding"; else email local-part; else userID.
const getCurrentUserName = (userProfile) => {
  const user = userProfile?.currentUser;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return fullName || user?.email?.split('@')[0] || user?.userID || 'User';
};

const getUserInitials = (userProfile) => getInitials(getCurrentUserName(userProfile));

// A PIPELINE_HIL session is "open" while it is PENDING (nobody has started it)
// or ACTIVE (someone has begun). Both float to the top of the chat list and
// keep the review highlight. Keying on PENDING alone would SINK the session the
// reviewer is actively working on the moment the HIL router flips it to ACTIVE,
// and strip its highlight at the same time.
const HIL_OPEN_STATUSES = ['PENDING', 'ACTIVE'];
const isOpenHilSession = (chat) => chat?.sessionType === 'PIPELINE_HIL'
  && HIL_OPEN_STATUSES.includes(chat?.status);

const MessageType = {
  USER: 'user',
  ASSISTANT: 'assistant',
  ASSISTANT_TOOL_USE: 'assistant_tool_use',
  TOOL_RESULT: 'tool_result',
  SYSTEM: 'system',
};

const ChatState = {
  WAITING_FOR_USER: 'waiting_for_user',
  AI_RESPONDING: 'ai_responding',
  AI_PROCESSING: 'ai_processing',
  AI_TOOL_USE: 'ai_tool_use',
  ERROR: 'error',
};

/* ---- Status indicator ---- */
function StatusDot({ state }) {
  if (!state) return null;

  const config = {
    [ChatState.WAITING_FOR_USER]: { label: 'Ready', cls: 'ready' },
    [ChatState.AI_PROCESSING]: { label: 'Thinking...', cls: 'processing' },
    [ChatState.AI_TOOL_USE]: { label: 'Using tools...', cls: 'processing' },
    [ChatState.AI_RESPONDING]: { label: 'Responding...', cls: 'processing' },
    [ChatState.ERROR]: { label: 'Error', cls: 'error' },
  };

  const c = config[state.state] || config[ChatState.WAITING_FOR_USER];

  return (
    <span className={`${styles.statusDot} ${styles[c.cls]}`} title={c.label}>
      <span className={styles.statusPulse} />
    </span>
  );
}

/* ---- Single message ---- */
// Files Livi saved in this reply (metadata.documents). A chip reopens the file
// beside the chat; the panel fetches the rest of the record by id.
const FORMAT_LABELS = { docx: 'Word', xlsx: 'Excel', pdf: 'PDF' };

function DocumentChips({ documents, onOpenDocument }) {
  const list = (Array.isArray(documents) ? documents : []).filter((d) => d && d.documentId);
  if (!list.length) return null;
  return (
    <div className={styles.fileChips}>
      {list.map((doc) => {
        const label = FORMAT_LABELS[(doc.format || '').toLowerCase()] || 'File';
        const title = doc.title || 'Document';
        return (
          <button
            key={doc.documentId}
            type="button"
            className={styles.fileChip}
            onClick={() => onOpenDocument && onOpenDocument(doc)}
            disabled={!onOpenDocument}
            aria-label={`Open ${label} file: ${title}`}
          >
            <span className={styles.fileChipFormat}>{label}</span>
            <span className={styles.fileChipTitle}>{title}</span>
          </button>
        );
      })}
    </div>
  );
}

// Thumbs on a reply. The rating saves on the click and the box only adds the why,
// so leaving the box empty still keeps a thumbs-down. A finished rating rests as
// one line; Change brings back the thumbs and the saved reason.
function ReplyFeedback({ message, sessionID, onSaved }) {
  const rating = message.my_feedback?.rating;
  const savedReason = message.my_feedback?.reason || '';
  const [boxOpen, setBoxOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const [reason, setReason] = useState(savedReason);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputId = `reply-feedback-${message.message_id}`;

  const save = async (nextRating, why) => {
    setSaving(true);
    setFailed(false);
    try {
      const result = await sendReplyFeedback({
        sessionID,
        message_id: message.message_id,
        timestamp: message.timestamp,
        rating: nextRating,
        ...(why ? { reason: why } : {}),
      });
      if (!result?.success) throw new Error('not saved');
      onSaved(message.message_id, result.my_feedback);
      return true;
    } catch (e) {
      setFailed(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finish = () => {
    setBoxOpen(false);
    setChanging(false);
  };

  const rateUp = async () => {
    if (rating === 'up' || await save('up')) {
      setReason('');
      finish();
    }
  };

  const rateDown = () => {
    setBoxOpen(true);
    if (rating !== 'down') save('down');
  };

  const sendReason = async () => {
    if (await save('down', reason.trim())) finish();
  };

  const skip = () => {
    setReason(savedReason);
    finish();
  };

  const change = () => {
    setChanging(true);
    setBoxOpen(rating === 'down');
  };

  if (rating && !boxOpen && !changing) {
    return (
      <div className={styles.replyFeedback}>
        <div className={styles.replyFeedbackDone}>
          <span role="status">
            Thanks. You marked this reply as {rating === 'up' ? 'good' : 'missed'}.
          </span>
          <button type="button" className={styles.replyFeedbackChange} onClick={change} aria-label="Change your feedback">
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.replyFeedback}>
      <div className={styles.replyFeedbackButtons} role="group" aria-label="Rate this reply">
        <button
          type="button"
          className={`${styles.replyFeedbackButton} ${rating === 'up' ? styles.replyFeedbackButtonOn : ''}`}
          onClick={rateUp}
          disabled={saving}
          aria-pressed={rating === 'up'}
          aria-label="Good reply"
          title="Good reply"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 10v12"/>
            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
          </svg>
        </button>
        <button
          type="button"
          className={`${styles.replyFeedbackButton} ${rating === 'down' ? styles.replyFeedbackButtonOn : ''}`}
          onClick={rateDown}
          disabled={saving}
          aria-pressed={rating === 'down'}
          aria-label="Reply missed"
          title="Reply missed"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17 14V2"/>
            <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>
          </svg>
        </button>
      </div>
      {boxOpen && (
        <div className={styles.replyFeedbackBox}>
          <label className={styles.replyFeedbackLabel} htmlFor={inputId}>What was wrong? (optional)</label>
          <textarea
            id={inputId}
            className={styles.replyFeedbackInput}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            rows={2}
          />
          <div className={styles.replyFeedbackActions}>
            <button type="button" className={styles.replyFeedbackAction} onClick={sendReason} disabled={saving || !reason.trim()}>
              Send
            </button>
            <button type="button" className={styles.replyFeedbackAction} onClick={skip}>
              Skip
            </button>
          </div>
        </div>
      )}
      {failed && <div className={styles.replyFeedbackError} role="alert">That didn't save. Please try again.</div>}
    </div>
  );
}

function Message({ message, currentUserId, currentUserName, currentUserInitials, onOpenDocument, sessionID, canRate, onFeedbackSaved }) {
  const { message_type, content, user_name, timestamp, tool_uses } = message;
  const time = timestamp
    ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  if (message_type === MessageType.USER) {
    // Attribute each bubble to its ACTUAL author, not the viewer. `user_name`
    // holds the author's userID; the backend resolves it to
    // user_display_name/initials so a shared session shows the right person.
    // For the current user's own messages — and legacy rows the backend
    // couldn't resolve — fall back to the live profile so the avatar stays in
    // sync with NavSidebar. (`!user_name` covers optimistic just-sent bubbles.)
    const isCurrentUser = !user_name || user_name === currentUserId;
    const displayName = isCurrentUser
      ? (currentUserName || message.user_display_name || user_name)
      : (message.user_display_name || user_name);
    const initials = isCurrentUser
      ? (currentUserInitials || message.initials || getInitials(message.user_display_name || user_name))
      : (message.initials || getInitials(message.user_display_name || user_name));
    return (
      <div className={styles.msg}>
        <div className={`${styles.msgAvatar} ${styles.msgAvatarUser}`} title={displayName}>
          {initials}
        </div>
        <div className={styles.msgBubbleUser}>
          {content}
        </div>
      </div>
    );
  }

  if (message_type === MessageType.ASSISTANT) {
    return (
      <div className={styles.msg}>
        <div className={`${styles.msgAvatar} ${styles.msgAvatarAI}`}>Livi</div>
        <div className={styles.msgBubbleAI}>
          <div className={styles.markdown}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {normalizeChatText(content) || ''}
            </ReactMarkdown>
          </div>
          <DocumentChips documents={message.metadata?.documents} onOpenDocument={onOpenDocument} />
          {canRate && content ? (
            <ReplyFeedback message={message} sessionID={sessionID} onSaved={onFeedbackSaved} />
          ) : null}
        </div>
      </div>
    );
  }

  if (message_type === MessageType.ASSISTANT_TOOL_USE) {
    const commentary = normalizeChatText(content);
    return (
      <div className={styles.msg}>
        <div className={`${styles.msgAvatar} ${styles.msgAvatarAI}`}>Livi</div>
        <div className={styles.msgBubbleTool}>
          <div className={styles.toolHeader}>Using tools</div>
          <div className={styles.toolChips}>
            {tool_uses && tool_uses.map((tool, idx) => (
              <Chip
                key={idx}
                label={formatToolName(tool.name)}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            ))}
          </div>
          {commentary && <div className={`${styles.toolContent} ${styles.markdown}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{commentary}</ReactMarkdown>
          </div>}
        </div>
      </div>
    );
  }

  if (message_type === MessageType.SYSTEM) {
    return (
      <div className={styles.systemMsg}>{content}</div>
    );
  }

  return null;
}

/* ---- Main SidePanelChat ---- */
export default function SidePanelChat({ open, onClose, onOpenDocument, onSessionChange, pendingMessage, onPendingMessageConsumed, pendingSend, onPendingSendConsumed, requestedSessionId, requestedSessionType, onRequestedSessionConsumed }) {
  const [sessionID, setSessionID] = useState(null);
  // `sessionType` mirrors the active session's type so the chat-API
  // calls can route to the right DDB table on the backend (case-DDB vs
  // system-DDB). Defaults to undefined → backend treats as CASE_WORK.
  // Set when a SYSTEM_WORK session was opened via AIAssistantContext.
  const [sessionType, setSessionType] = useState(undefined);
  // EVERY session switch goes through here so the type always belongs to the
  // session it was set for. A type left over from a SYSTEM_WORK chat made the
  // next case chat load from the system table — it showed up blank (dev
  // 2026-09-27: an existing case interview opened empty after "Review with Livi").
  const openSession = useCallback((id, type = undefined) => {
    setSessionID(id);
    setSessionType(type);
  }, []);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const { userProfile } = useContext(UserProfileContext);

  // Sessions can be shared across permitted users, so each message is
  // attributed to its own author (resolved server-side). We still keep the
  // live-profile name/initials for the *current* user's own bubbles so those
  // stay in sync with NavSidebar; `currentUserId` selects whose messages those
  // apply to.
  const currentUserId = userProfile?.currentUser?.userID;
  const currentUserName = getCurrentUserName(userProfile);
  const currentUserInitials = getUserInitials(userProfile);

  // Fetch chat list
  const { data: chatListData } = useGetChatList(true);
  const chatList = chatListData?.chats || [];

  // Initialize with the most recent NON-TOPIC, NON-HIL chat, or a new chat.
  // Topic sessions are dedicated single-topic investigation threads opened by
  // clicking a topic card on the dashboard. The user's mental model when
  // re-entering the portal is to land on the dashboard / their general
  // chat — NOT to be plunged back into the deep topic thread they last
  // touched. Without this filter, a user who closed the tab mid-topic
  // would re-open the portal and find themselves still inside that
  // topic's investigation chat with no obvious "I'm here, not on the
  // dashboard" signal. PIPELINE_HIL is also excluded: HIL sessions are
  // case-shared and their center-panel renders are published globally per
  // session, so auto-landing on a colleague's active HIL session pulls
  // their live scan editor into this user's center panel and swaps out
  // whatever they're actually working on. HIL remains reachable via the
  // dashboard's "Pending HIL Reviews" tile and the chat-list menu. Other
  // session types (CASE_WORK, CASE_INTERVIEW) DO auto-restore — those
  // represent the user's general / ongoing work, not a per-artifact
  // deep-dive.
  useEffect(() => {
    if (!initialized && chatListData) {
      const restorable = chatList.find(
        (c) =>
          c.sessionType !== 'TOPIC_INVESTIGATION' &&
          c.sessionType !== 'PIPELINE_HIL'
      );
      if (restorable) {
        openSession(restorable.sessionID);
      } else {
        openSession(generatesessionID());
      }
      setInitialized(true);
    }
  }, [chatListData, chatList, initialized, openSession]);

  // External session selection — e.g., the dashboard's "Pending HIL Reviews"
  // card click requests opening a specific session. AppShell sets
  // `requestedSessionId` and we adopt it once on consumption.
  useEffect(() => {
    if (requestedSessionId && requestedSessionId !== sessionID) {
      // Adopt the caller-supplied sessionType too — required for
      // SYSTEM_WORK sessions so chat-API calls route to system-DDB.
      // Defaults to undefined (= CASE_WORK on the backend) when the
      // caller doesn't specify, preserving existing behavior.
      openSession(requestedSessionId, requestedSessionType);
      setInitialized(true);
      if (onRequestedSessionConsumed) onRequestedSessionConsumed();
    } else if (requestedSessionId) {
      // Already on this session — still take the caller's type (a stale one
      // is exactly what blanked the chat) and mark consumed so AppShell clears state.
      setSessionType(requestedSessionType);
      if (onRequestedSessionConsumed) onRequestedSessionConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSessionId]);

  // Notify parent of session changes (center panel subscription + the composite
  // session-context fetch, which needs the type to read the right table).
  useEffect(() => {
    if (onSessionChange && sessionID) {
      onSessionChange(sessionID, sessionType);
    }
  }, [sessionID, sessionType, onSessionChange]);

  // Populate input from center-panel copy-to-chat. Topic-investigation
  // sessions DON'T flow through pendingMessage — they go through
  // openChatSession(sessionID) instead, so each topic discussion lives in
  // its own dedicated session.
  useEffect(() => {
    if (!pendingMessage) return;
    setInput(pendingMessage);
    if (onPendingMessageConsumed) onPendingMessageConsumed();
    // Put the cursor in the chat box after the pre-filled text: a center-panel "Change" filled it silently and
    // the user couldn't tell where it went (dev check 2026-10-24). After the render that sets the value.
    setTimeout(() => {
      const el = textareaRef.current;
      if (el && !el.disabled) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 0);
  }, [pendingMessage, onPendingMessageConsumed]);

  // Fetch chat history. session_type hint is forwarded to the backend
  // when set (SYSTEM_WORK) so /chat/messages reads from the system DDB
  // table — otherwise the backend defaults to per-case-hil and the
  // SYSTEM_WORK session appears empty.
  const parameters = useMemo(
    () => sessionID
      ? {
          sessionID,
          include_state: 'true',
          ...(sessionType ? { session_type: sessionType } : {}),
        }
      : {},
    [sessionID, sessionType]
  );
  const { data, mutate } = useGetChatHistory(parameters, !!sessionID);

  // Subscribe to real-time events. onSwitchSession handles agent-driven
  // navigation: when the user asks the agent to "take me to the topic"
  // from inside the case interview, the open_topic_in_dedicated_chat tool
  // publishes a SWITCH_SESSION event on this session's chat channel. We
  // swap sessionID; the existing useGetChatHistory + UI re-renders for
  // the new session. onSessionChange fires automatically downstream.
  useChatSubscription(sessionID, mutate, {
    enabled: !!sessionID,
    onSwitchSession: (newSessionID) => {
      if (newSessionID && newSessionID !== sessionID) {
        openSession(newSessionID);
      }
    },
  });

  // Keep a saved rating in the cached history; a live-pushed message never
  // carries one, and the next fetch reads it back from the server.
  const handleFeedbackSaved = useCallback((messageId, myFeedback) => {
    mutate((current) => current && {
      ...current,
      messages: (current.messages || []).map((m) => (
        m.message_id === messageId ? { ...m, my_feedback: myFeedback } : m
      )),
    }, { revalidate: false });
  }, [mutate]);

  const messages = data?.messages || [];
  const chatState = data?.state || null;
  // Live streaming overlay (best-effort token deltas, keyed by turn). Not
  // part of `messages` — the committed assistant message replaces it when the
  // turn commits (see useChatSubscription). null, or empty text with a seq
  // floor that drops late deltas, when not streaming — render only `text`.
  const streaming = data?.streaming || null;
  // Hide tool-result messages (rendered via the assistant's tool-use chips
  // instead) and bootstrap messages (system-routed triggers like the one
  // that opens a topic-investigation session — they're not user content).
  // useMemo so the filtered array's identity is stable across keystroke
  // re-renders — the auto-scroll effect below depends on a stable signal,
  // and a fresh array on every render caused jitter while typing (smooth
  // scroll animation re-fired against textarea-height reflows).
  const displayMessages = useMemo(() => messages.filter((msg) => {
    if (msg.message_type === MessageType.TOOL_RESULT) return false;
    if (msg.metadata?.bootstrap === true) return false;
    return true;
  }), [messages]);

  // Agent is "busy" — disables the input + shows the typing indicator until
  // the agent hands the turn back. Includes AI_RESPONDING (the agent is
  // actively generating its reply): without it, the indicator drops and
  // input re-enables for the entire reply window, which lets the user fire a
  // second message that the agent processes concurrently with the first.
  const isProcessing = chatState?.state === ChatState.AI_PROCESSING ||
    chatState?.state === ChatState.AI_TOOL_USE ||
    chatState?.state === ChatState.AI_RESPONDING;

  // Re-focus the textarea when the agent finishes a turn. While the
  // agent is processing, the textarea is `disabled`, which blurs it.
  // Without this effect, the user has to click back into the field
  // every time they want to send the next message — annoying for the
  // back-and-forth interview pattern. Triggers only on the
  // true → false transition (not on every render where isProcessing
  // happens to be false), so it doesn't steal focus from other
  // elements during normal typing or page navigation.
  const wasProcessingRef = useRef(false);
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      textareaRef.current?.focus();
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Auto-scroll only when the visible message COUNT changes (i.e. a new
  // message arrived) — not on every render, and not on identity-only
  // churn of the messages array. Typing in the textarea must NOT trigger
  // a scroll: a smooth-scroll animation fighting the textarea's
  // auto-grow reflow caused the chat window to jitter on every keystroke.
  const lastSeenCountRef = useRef(0);
  useEffect(() => {
    if (displayMessages.length !== lastSeenCountRef.current) {
      lastSeenCountRef.current = displayMessages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [displayMessages.length]);

  // Keep the live streaming bubble in view as it grows. 'auto' (not 'smooth')
  // so the ~5/sec delta updates don't pile up scroll animations.
  useEffect(() => {
    if (streaming?.text) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [streaming?.text]);

  // New chat
  const handleNewChat = () => {
    openSession(generatesessionID());
    setMenuAnchor(null);
  };

  // Switch chat (the list holds case chats only — never SYSTEM_WORK)
  const handleSelectChat = (id) => {
    openSession(id);
    setMenuAnchor(null);
  };

  // Send a message bypassing the input box. Used by handleSend (which clears
  // input first) AND by the auto-send path for protocol-prefixed pending
  // messages (e.g., `@view-topic <id>` from the dashboard click-through).
  const sendMessageCore = useCallback(async (messageContent, extraMetadata) => {
    if (!messageContent || isSending || !sessionID) return;
    setIsSending(true);

    const optimisticMessage = {
      message_id: `temp_${Date.now()}`,
      message_type: MessageType.USER,
      user_name: userProfile?.currentUser?.userID || 'User',
      initials: getUserInitials(userProfile),
      content: messageContent,
      timestamp: new Date().toISOString(),
      sessionID,
    };

    try {
      mutate(
        (currentData) => {
          if (!currentData) {
            return {
              sessionID,
              mode: 'conversation',
              messages: [optimisticMessage],
              message_count: 1,
              total_messages: 1,
              is_empty_chat: false,
              state: {
                session_id: sessionID,
                state: ChatState.AI_PROCESSING,
                last_updated: new Date().toISOString(),
                last_message_id: optimisticMessage.message_id,
              },
            };
          }
          return {
            ...currentData,
            messages: [...(currentData.messages || []), optimisticMessage],
            message_count: (currentData.message_count || 0) + 1,
            state: {
              ...currentData.state,
              state: ChatState.AI_PROCESSING,
              last_updated: new Date().toISOString(),
              last_message_id: optimisticMessage.message_id,
            },
          };
        },
        false
      );

      // metadata.sessionType is read server-side by post_chat_message
      // and threaded through to ChatHistoryManager + initiate_chat_agent
      // so the SQS-driven chat_agent consumer also picks the right
      // table. Undefined → backend defaults to CASE_WORK (existing
      // behavior, all current chat flows).
      // extraMetadata rides on the user's message (e.g. the intake checklist's ticked items).
      const metadata = { ...(sessionType ? { sessionType } : {}), ...(extraMetadata || {}) };
      await sendChatMessageV2({
        message: messageContent,
        sessionID,
        ...(Object.keys(metadata).length ? { metadata } : {}),
      });
    } catch (err) {
      console.error('Error sending message:', err);
      mutate();
    } finally {
      setIsSending(false);
    }
  }, [sessionID, sessionType, isSending, mutate, userProfile]);

  // Send a message the user chose in the center panel (the intake checklist's Save) as their own message.
  // Waits until nothing is in flight so the send can't be dropped.
  useEffect(() => {
    if (!pendingSend || isSending || !sessionID) return;
    sendMessageCore(pendingSend.message, pendingSend.metadata);
    if (onPendingSendConsumed) onPendingSendConsumed();
  }, [pendingSend, isSending, sessionID, sendMessageCore, onPendingSendConsumed]);

  // Send message (user pressed Enter / clicked Send)
  const handleSend = async () => {
    if (!input.trim() || isSending || !sessionID) return;
    const messageContent = input.trim();
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    await sendMessageCore(messageContent);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  return (
    <aside className={`${styles.panel} ${open ? styles.panelOpen : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Livi AI</h2>
          <StatusDot state={chatState} />
        </div>
        <div className={styles.headerActions}>
          {/* Chat switcher */}
          <button
            className={styles.headerBtn}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            title="Switch chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            PaperProps={{ sx: { width: 300, maxHeight: 400 } }}
          >
            <MenuItem onClick={handleNewChat}>
              <AddIcon sx={{ mr: 1, fontSize: 18 }} />
              <ListItemText primary="New Chat" />
            </MenuItem>
            <Divider />
            {chatList.length > 0 ? (
              [...chatList].sort((a, b) => {
                // Open (PENDING or ACTIVE) PIPELINE_HIL sessions float to top
                const aHIL = isOpenHilSession(a) ? 0 : 1;
                const bHIL = isOpenHilSession(b) ? 0 : 1;
                if (aHIL !== bHIL) return aHIL - bHIL;
                return 0; // preserve existing order otherwise
              }).map((chat) => {
                const isHIL = chat.sessionType === 'PIPELINE_HIL';
                const isOpenHIL = isOpenHilSession(chat);
                // Still keyed on PENDING specifically: "needs attention" means
                // nobody has picked it up yet. Once someone has (ACTIVE), the
                // row keeps its highlight and top placement but drops the nag.
                const isPending = chat.status === 'PENDING';
                return (
                <MenuItem
                  key={chat.sessionID}
                  onClick={() => handleSelectChat(chat.sessionID)}
                  selected={chat.sessionID === sessionID}
                  sx={isOpenHIL ? { borderLeft: '3px solid', borderColor: 'error.main', bgcolor: 'error.50' } : {}}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isHIL && <Chip label="Review" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        {chat.preview || 'New conversation'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(chat.last_updated).toLocaleDateString()}
                        {isHIL && isPending && ' — needs attention'}
                      </Typography>
                    }
                  />
                </MenuItem>
              );})
            ) : (
              <MenuItem disabled>
                <ListItemText primary="No previous chats" />
              </MenuItem>
            )}
          </Menu>

          {/* New chat button */}
          <button
            className={styles.headerBtn}
            onClick={handleNewChat}
            title="New chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="M12 5v14"/>
            </svg>
          </button>

          {/* Close button */}
          <button
            className={styles.headerBtn}
            onClick={onClose}
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="18" height="18" x="3" y="3" rx="2"/>
              <path d="M15 3v18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className={styles.messages}>
        {!initialized || !sessionID ? (
          <div className={styles.loading}>
            <CircularProgress size={28} />
          </div>
        ) : displayMessages.length === 0 && !isProcessing && !streaming?.text ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/>
              </svg>
            </div>
            <div className={styles.emptyTitle}>Start a conversation</div>
            <div className={styles.emptyHint}>Ask about transactions, patterns, or case analysis</div>
          </div>
        ) : (
          <>
            {displayMessages.map((message) => (
              <Message key={message.message_id} message={message} currentUserId={currentUserId} currentUserName={currentUserName} currentUserInitials={currentUserInitials} onOpenDocument={onOpenDocument} sessionID={sessionID} canRate={sessionType !== 'SYSTEM_WORK'} onFeedbackSaved={handleFeedbackSaved} />
            ))}
            {streaming?.text ? (
              // Live streaming bubble — replaced by the committed assistant
              // message the moment the turn commits.
              <div className={styles.msg}>
                <div className={`${styles.msgAvatar} ${styles.msgAvatarAI}`}>Livi</div>
                <div className={styles.msgBubbleAI}>
                  <div className={styles.markdown}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {streaming.text}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ) : isProcessing && (
              <div className={styles.msg}>
                <div className={`${styles.msgAvatar} ${styles.msgAvatarAI}`}>Livi</div>
                <div className={styles.msgBubbleAI}>
                  <div className={styles.typingIndicator}>
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className={styles.inputArea}>
        <div className={styles.inputWrapper}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            placeholder={isProcessing ? 'AI is thinking...' : 'Ask about this case...'}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={isSending || isProcessing}
          />
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={!input.trim() || isSending || isProcessing}
            aria-label="Send message"
          >
            {isSending ? (
              <CircularProgress size={14} sx={{ color: 'white' }} />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}
